import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import {
  SuperAdminCompanyScope,
  getSuperAdminScope,
  storeSuperAdminScope,
} from '../utils/superAdminCompanyScope';

export type { SuperAdminCompanyScope };

interface SuperAdminCompanyContextType {
  scope: SuperAdminCompanyScope;
  setScope: (scope: SuperAdminCompanyScope) => void;
}

const SuperAdminCompanyContext = createContext<SuperAdminCompanyContextType | undefined>(undefined);

export const useSuperAdminCompany = (): SuperAdminCompanyContextType => {
  const context = useContext(SuperAdminCompanyContext);
  if (context === undefined) {
    throw new Error('useSuperAdminCompany must be used within a SuperAdminCompanyProvider');
  }
  return context;
};

interface SuperAdminCompanyProviderProps {
  children: ReactNode;
}

export const SuperAdminCompanyProvider: React.FC<SuperAdminCompanyProviderProps> = ({ children }) => {
  const { user, loading } = useAuth();
  const [scope, setScopeState] = useState<SuperAdminCompanyScope>(() => getSuperAdminScope());

  const setScope = useCallback((next: SuperAdminCompanyScope) => {
    storeSuperAdminScope(next);
    setScopeState(next);
  }, []);

  // Resync from storage whenever the logged-in user changes (login, logout, switching
  // accounts in the same tab) — protects against the same class of stale-in-memory-
  // state bug found and fixed in ResellerCompanyContext: logout() clears storage
  // directly via clearSuperAdminScope() without going through this context's own
  // setter, so without this resync a previous session's single-company pick could
  // survive in memory and leak into whichever user logs in next in the same tab.
  useEffect(() => {
    if (loading) return;
    setScopeState(getSuperAdminScope());
  }, [user, loading]);

  return (
    <SuperAdminCompanyContext.Provider value={{ scope, setScope }}>
      {children}
    </SuperAdminCompanyContext.Provider>
  );
};

export default SuperAdminCompanyContext;
